import { useTranslation } from 'react-i18next'
import {
  FiltersPanel,
  SearchField,
  type PropertyFiltersValues,
  type UpdateSearch,
} from '@/features/reservation/components/PropertyFilters'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { DateRangePicker } from '@/shared/components/cse/DateRangePicker'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import { HorizonGuestStepper } from './HorizonGuestStepper'

const SearchIcon = createLazyThemedIcon('search')
const FilterIcon = createLazyThemedIcon('sliders')

type HorizonSearchBarProps = {
  city: string
  availableFrom: string
  availableTo: string
  flex: number
  guests: string
  filterCount: number
  panelValues: PropertyFiltersValues
  updateSearch: UpdateSearch
  clearAll: () => void
}

export function HorizonSearchBar({
  city,
  availableFrom,
  availableTo,
  flex,
  guests,
  filterCount,
  panelValues,
  updateSearch,
  clearAll,
}: HorizonSearchBarProps) {
  const { t } = useTranslation()
  const guestCount = Math.max(0, Math.trunc(Number(guests) || 0))

  const onGuestsChange = (value: number) => {
    updateSearch({ guests: value > 0 ? String(value) : null })
  }

  return (
    <section
      aria-labelledby="reservation-search-heading"
      className="horizon-search-bar mb-4 rounded-[var(--brand-radius-button)] border border-[var(--brand-border)] bg-[var(--brand-background)] p-2 shadow-[var(--brand-shadow-search-bar)] lg:min-h-14 lg:p-0"
    >
      <h2 id="reservation-search-heading" className="sr-only">
        {t('reservation.properties.filters.search_panel_title')}
      </h2>

      <div className="hidden h-14 items-stretch divide-x divide-[var(--brand-border)] lg:grid lg:grid-cols-[1.2fr_1.6fr_1fr_auto]">
        <div className="flex min-w-0 flex-col justify-center px-4">
          <SearchField
            id="filter-city"
            icon="map"
            label={t('reservation.properties.filters.city')}
            value={city}
            placeholder={t('reservation.properties.filters.city_placeholder')}
            onChange={(value) => updateSearch({ city: value || null })}
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center px-4">
          <DateRangePicker
            from={availableFrom || null}
            to={availableTo || null}
            flexDays={flex}
            onFlexChange={(n) => updateSearch({ flex: n > 0 ? String(n) : null })}
            onChange={({ from, to }) => updateSearch({ available_from: from, available_to: to })}
          />
        </div>
        <div className="flex min-w-0 items-center px-3">
          <HorizonGuestStepper
            label={t('reservation.properties.filters.guests')}
            value={guestCount > 0 ? guestCount : 1}
            min={1}
            decrementLabel={t('reservation.properties.filters.guests_decrease')}
            incrementLabel={t('reservation.properties.filters.guests_increase')}
            onChange={onGuestsChange}
          />
        </div>
        <div className="flex items-center gap-2 px-3">
          <Button type="button" variant="ghost" size="sm" className="text-[var(--brand-text-secondary)]" onClick={clearAll}>
            {t('reservation.properties.filters.clear_all')}
          </Button>
          <Button
            type="button"
            size="icon"
            className="size-11 shrink-0 rounded-full bg-[var(--brand-primary-strong)] text-[var(--brand-on-primary)] hover:bg-[var(--brand-primary-hover)]"
            aria-label={t('reservation.properties.filters.apply')}
            onClick={() => updateSearch({})}
          >
            <SearchIcon className="size-5" aria-hidden />
          </Button>
        </div>
      </div>

      <div className="lg:hidden">
        <Dialog>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="horizon-search-mobile-trigger min-h-14 w-full justify-between rounded-[var(--brand-radius-button)] border-[var(--brand-border)] px-4"
            >
              <span className="flex min-w-0 items-center gap-3">
                <SearchIcon className="size-5 shrink-0" aria-hidden />
                <span className="min-w-0 truncate text-left">
                  {city || t('reservation.properties.filters.city_placeholder')}
                </span>
              </span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--brand-text-secondary)]">
                <FilterIcon className="size-4" aria-hidden />
                {t('reservation.properties.filters.mobile_open', { count: filterCount })}
              </span>
            </Button>
          </DialogTrigger>
          <DialogContent
            closeAriaLabel={t('reservation.properties.filters.mobile_close')}
            className="horizon-search-mobile-panel left-0 top-0 flex h-dvh max-w-none translate-x-0 translate-y-0 grid-rows-none flex-col gap-0 rounded-none border-0 p-0"
          >
            <DialogHeader className="shrink-0 border-b border-[var(--brand-border-soft)] px-5 py-4 text-left">
              <DialogTitle>{t('reservation.properties.filters.search_panel_title')}</DialogTitle>
              <DialogDescription>{t('reservation.properties.filters.description')}</DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid gap-4">
                <SearchField
                  id="mobile-filter-city"
                  icon="map"
                  label={t('reservation.properties.filters.city')}
                  value={city}
                  placeholder={t('reservation.properties.filters.city_placeholder')}
                  onChange={(value) => updateSearch({ city: value || null })}
                />
                <DateRangePicker
                  from={availableFrom || null}
                  to={availableTo || null}
                  flexDays={flex}
                  onFlexChange={(n) => updateSearch({ flex: n > 0 ? String(n) : null })}
                  onChange={({ from, to }) => updateSearch({ available_from: from, available_to: to })}
                />
                <HorizonGuestStepper
                  label={t('reservation.properties.filters.guests')}
                  value={guestCount > 0 ? guestCount : 1}
                  min={1}
                  decrementLabel={t('reservation.properties.filters.guests_decrease')}
                  incrementLabel={t('reservation.properties.filters.guests_increase')}
                  onChange={onGuestsChange}
                />
              </div>
              <div className="mt-5 border-t border-[var(--brand-border-soft)] pt-5">
                <FiltersPanel values={panelValues} updateSearch={updateSearch} />
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-[var(--brand-border-soft)] bg-[var(--brand-surface)] px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button type="button" variant="outline" className="flex-1" onClick={clearAll}>
                {t('reservation.properties.filters.clear_all')}
              </Button>
              <DialogClose asChild>
                <Button type="button" className="flex-1">
                  {t('reservation.properties.filters.apply')}
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  )
}
